<script>
import axios from "axios";
import config from "@/config.js";

export default {
  inheritAttrs: false,
  props: {
    value: String, // url path of image
    label: {
      type: String,
    },
    name: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    readonly: {
      type: Boolean,
    },
    required: {
      type: Boolean,
    },
    rules: {
      type: String,
    },
    overrideError: Boolean,
  },
  data() {
    return {
      innerValue: this.value || '',
      isUploading: false,
      uploadProgress: 0,
    }
  },
  methods: {
    onChange(e) {
      const file = e.target.files[0]

      // handle cancel select file
      if (!file) {
        this.innerValue = ''
        return;
      }

      if (file.size > 10485760) {
        this.$vs.notification({
          color: "danger",
          position: "top-right",
          text: this.$t('product.imageSize10'),
          duration: 5000,
        });
        return;
      }

      if (!file.type.startsWith("image/")) {
        this.$vs.notification({
          color: "danger",
          position: "top-right",
          text: 'Please select valid image format',
          duration: 5000,
        });
        return;
      }
      
      const formData = new FormData()
      formData.append('file', file)
      
      this.uploadFile(formData)
    },
    async uploadFile(formData) {
      try {
        this.isUploading = true;
        this.uploadProgress = 0;

        const endpoint = config.api + '/product/upload-file'
        const response = await axios.post(endpoint, formData, {
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          },
        });

        if (response.data.success) {
          this.innerValue = response.data.data
          this.isUploading = false;
        }
      } catch (e) {
        console.error("Error uploading image:", e);
        this.isUploading = false;
        this.uploadProgress = 0;

        this.$vs.notification({
          color: "danger",
          position: "top-right",
          text: "Error uploading image",
          duration: 5000,
        });
      }
    },
  },
  watch: {
    innerValue: function (v) {
      this.$emit('input', v)
    },
    value(value) {
      if (this.innerValue !== value) this.innerValue = value
    }
  },
  computed: {
    textPlaceholder() {
      return this.required && !this.label ? `${this.placeholder} *` : this.placeholder
    },
  },
}
</script>

<template>
  <ValidationProvider ref="provider" :rules="`${!rules ? rules + '|image' : undefined}`" v-slot="{ errors, passed, failed }">
    <div class="classic-form__input">
      <label v-if="label">
        {{ label }}&nbsp;
        <svg v-if="required" width="7" height="6" viewBox="0 0 7 6" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.71733 5.86375L2.81179 3.76574L1.0419 4.90423L0.5 3.95963L2.37926 3.00011L0.5 2.04059L1.0419 1.09599L2.81179 2.23449L2.71733 0.136475H3.79616L3.7017 2.23449L5.47159 1.09599L6.01349 2.04059L4.13423 3.00011L6.01349 3.95963L5.47159 4.90423L3.7017 3.76574L3.79616 5.86375H2.71733Z"
            fill="#E12121" />
        </svg>
      </label>
      <input
        ref="input"
        type="file"
        @change="onChange"
        :data-passed="(passed && innerValue !== '') || Boolean(innerValue)"
        :data-failed="overrideError || failed"
        :placeholder="textPlaceholder"
        :name="name"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
      />
      <span v-if="errors[0]" class="classic-form__input__error-message scroll-to-error-message">{{ errors[0] || '' }}</span>
      <!-- <span v-if="isUploading" class="classic-form__input__progress">{{ uploadProgress }}%</span> -->
    </div>
  </ValidationProvider>
</template>

<style lang="scss" scoped>
.classic-form__input {
  position: relative;
  display: flex;
  flex-direction: column;

  input {
    position: relative;
    height: 44px;
    width: 100%;
    padding: 12px 15px;
    background-color: #FDFDFF;
    outline: 2px solid #DADADA !important;
    outline-offset: -2px !important;
    border: none !important;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.01em;

    &::placeholder {
      color: #6C7E95;
    }

    &:focus {
      border: none !important;
      outline-color: #2E47BA !important;

      &::placeholder {
        color: transparent;
      }
    }

    @mixin icon-validation {
      background-repeat: no-repeat;
      background-position: top 9px right 9.5px;
    }

    &[data-passed=true]:focus {
      @include icon-validation;
      padding-right: 48.5px;
      background-image: url("data:image/svg+xml,%0A%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M2.25 12C2.25 6.61522 6.61522 2.25 12 2.25C17.3848 2.25 21.75 6.61522 21.75 12C21.75 17.3848 17.3848 21.75 12 21.75C6.61522 21.75 2.25 17.3848 2.25 12ZM12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75ZM15.6723 9.45266C15.9652 9.74556 15.9652 10.2204 15.6723 10.5133L11.4053 14.7803C11.1124 15.0732 10.6376 15.0732 10.3447 14.7803L8.89016 13.3258C8.59727 13.0329 8.59727 12.5581 8.89016 12.2652C9.18306 11.9723 9.65793 11.9723 9.95083 12.2652L10.875 13.1893L14.6117 9.45266C14.9046 9.15977 15.3794 9.15977 15.6723 9.45266Z' fill='%2314AD4D'/%3E%3C/svg%3E%0A");
    }

    &[data-failed=true] {
      @include icon-validation;
      padding-right: 48.5px;
      background-image: url("data:image/svg+xml,%0A%3Csvg width='24' height='25' viewBox='0 0 24 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 12.5V8M12 15.8354V15.875M21 12.5C21 17.4706 16.9706 21.5 12 21.5C7.02944 21.5 3 17.4706 3 12.5C3 7.52944 7.02944 3.5 12 3.5C16.9706 3.5 21 7.52944 21 12.5Z' stroke='%23F53535' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A") !important;
      outline-color: #E12121 !important;
    }
  }

  label {
    display: flex;
    align-items: center;
    color: #203551 !important;
    font-weight: 500 !important;
    font-size: 14px !important;
    line-height: 20px !important;
    letter-spacing: -0.01em !important;
    margin-bottom: 4px !important;
  }

  &__error-message {
    margin-top: 3px;
    display: inline-block;
    font-weight: 400;
    font-size: 10px;
    line-height: 14px;
    color: #E12121;
  }
  
  .scroll-to-error-message {
    scroll-margin: 84px;
  }

  &__progress {
    position: absolute;
    right: 15px;
    bottom: 11px;
    color: #6C7E95;
  }
}
</style>